import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { personLabel } from '../../components/ApprovalLevels.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

interface PrivilegedAccessRow {
  readonly privilegedAccessId: string;
  readonly subjectName: string | null;
  readonly subjectCode: string | null;
  readonly privilegeType: string;
  readonly trustLevel: string;
  readonly trustScore: string;
  readonly status: string;
  readonly mfaVerified: boolean;
  readonly anomalyFlag: boolean;
}

export function M19PrivilegedAccessDashboardPage(): ReactNode {
  const { decimal } = useBusinessNumber();
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<PrivilegedAccessRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiRequest<{ data: PrivilegedAccessRow[] }>('/v1/platform/privileged-access', { token })
      .then((r) => { if (alive) setRows(r.data); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  if (error) return <p role="alert">{error}</p>;
  if (!rows) return <p>{t('m19.privilegedAccess.loading')}</p>;
  return (
    <main aria-label="privileged-access-dashboard">
      <h1>{t('m19.privilegedAccess.title')}</h1>
      <table aria-label="privileged-access-table">
        <thead>
          <tr>
            <th>{t('m19.privilegedAccess.colUser')}</th>
            <th>{t('m19.privilegedAccess.colPrivilege')}</th>
            <th>{t('m19.privilegedAccess.colTrust')}</th>
            <th>{t('m19.privilegedAccess.colScore')}</th>
            <th>{t('m19.privilegedAccess.colStatus')}</th>
            <th>{t('m19.privilegedAccess.colMfa')}</th>
            <th>{t('m19.privilegedAccess.colAnomaly')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.privilegedAccessId}>
              <td>{personLabel(p.subjectName, p.subjectCode) ?? '—'}</td>
              <td>{p.privilegeType}</td>
              <td>{p.trustLevel}</td>
              <td>{decimal(p.trustScore)}</td>
              <td>{p.status}</td>
              <td>{p.mfaVerified ? t('m19.privilegedAccess.yes') : t('m19.privilegedAccess.no')}</td>
              <td>{p.anomalyFlag ? t('m19.privilegedAccess.yes') : t('m19.privilegedAccess.no')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </main>
  );
}
